import { Box, IconButton, Tooltip } from '@material-ui/core';
import { SButton, SETVIImage } from '@setvi/shared/components';
import { UserIcon } from '@setvi/shared/components/sicons';
import { useRef } from 'react';
import CloseRoundedIcon from '@material-ui/icons/CloseRounded';
import { enqueueSnackbar } from 'notistack';
import { useTooltipStyles } from '../../style';

const DEFAULT_IMAGE =
  'https://setvi.azureedge.net/publicdev/DefaultUserPhoto.png';

type PhotoSize = 'small' | 'normal' | 'large';

const sizeMap: Record<PhotoSize, number> = {
  small: 64,
  normal: 92,
  large: 126
};

const iconButtonSizeMap: Record<
  PhotoSize,
  { padding: number; iconSize: number; top: number; right: number }
> = {
  small: { padding: 3, iconSize: 16, top: 3, right: -8 },
  normal: { padding: 5, iconSize: 16, top: 10, right: -6 },
  large: { padding: 6, iconSize: 18, top: 12, right: -4 }
};

const userIconMap: Record<PhotoSize, string> = {
  small: 'scale(1)',
  normal: 'scale(1.4)',
  large: 'scale(1.8)'
};

const ALLOWED_IMAGE_TYPES = [
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/bmp',
  'image/svg+xml'
];

interface PhotoProps {
  handleChange: (name: string, photo: string, file?: File) => void;
  photo?: string;
  name?: string;
  alt?: string;
  size?: PhotoSize;
}

const Photo = ({
  handleChange,
  photo,
  name = 'photo',
  alt,
  size = 'small'
}: PhotoProps) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const tooltipClasses = useTooltipStyles();

  const handlePhotoUpload = (file: File) => {
    // Convert file to base64 or handle upload as needed
    const reader = new FileReader();
    reader.onloadend = () => {
      handleChange(name, reader.result as string, file);
    };

    reader.readAsDataURL(file);
  };

  const handleFileInputChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
      enqueueSnackbar(
        'Invalid file type. Please upload a JPG, PNG, GIF, BMP, or SVG image.',
        {
          variant: 'error',
          autoHideDuration: 8000
        }
      );
      return;
    }

    handlePhotoUpload(file);
  };

  const handleAddPhotoClick = () => {
    fileInputRef.current?.click();
  };

  const handleRemovePhoto = () => {
    handleChange(name, '');
  };

  return (
    <>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg, image/png, image/gif, image/bmp, image/svg+xml"
        style={{ display: 'none' }}
        onChange={handleFileInputChange}
      />
      <Box
        style={{
          display: 'flex',
          alignItems: 'flex-end',
          gap: 4
        }}>
        <Box
          style={{
            borderRadius: '50%',
            background: '#1E96FC',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: sizeMap[size],
            height: sizeMap[size],
            border: '2px solid #EEF0F2',
            position: 'relative'
          }}>
          {!photo || photo === DEFAULT_IMAGE ? (
            <UserIcon
              htmlColor="white"
              style={{
                transform: userIconMap[size]
              }}
            />
          ) : (
            <>
              <Box
                style={{
                  width: '100%',
                  height: '100%',
                  overflow: 'hidden',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                <SETVIImage
                  style={{
                    width: '100%',
                    height: '100%'
                  }}
                  fit="cover"
                  radius="50%"
                  src={photo || ''}
                  alt={alt || 'Photo'}
                />
              </Box>
              <Tooltip
                title="Remove photo"
                arrow
                placement="right"
                classes={tooltipClasses}>
                <IconButton
                  onClick={handleRemovePhoto}
                  style={{
                    background: '#20262D',
                    position: 'absolute',
                    padding: iconButtonSizeMap[size].padding,
                    margin: 0,
                    top: iconButtonSizeMap[size].top,
                    right: iconButtonSizeMap[size].right
                  }}>
                  <CloseRoundedIcon
                    style={{
                      width: iconButtonSizeMap[size].iconSize,
                      height: iconButtonSizeMap[size].iconSize
                    }}
                    htmlColor="white"
                  />
                </IconButton>
              </Tooltip>
            </>
          )}
        </Box>

        <SButton
          color="default"
          variant="outlined"
          onClick={handleAddPhotoClick}
          style={{
            borderRadius: 120,
            minHeight: 0,
            height: 'auto',
            padding: '2px 12px',
            margin: 0,
            color: '#5D7083',
            fontSize: 12,
            fontWeight: 600,
            borderColor: '#AFBAC6'
          }}>
          {!photo || photo === DEFAULT_IMAGE ? 'Add Photo' : 'Replace Photo'}
        </SButton>
      </Box>
    </>
  );
};

export default Photo;
